import { useEffect, useState } from 'react'
import { runningBundle } from './app-version'

// What build is this tab running, and when was it released.
//
// Every value already exists at runtime, so there is no version constant to
// remember to bump and nothing that can drift out of date:
//
//   version   REACT_APP_VERSION, set from $npm_package_version in .env — the
//             same value already sent as appVersion in the edxr-user-agent header
//   env       REACT_APP_ENV — DEV / PROD / LOCAL per env file
//   hash      the content hash off this tab's own <script> tag, which is the
//             build identity CRA already generates and the update banner compares
//   released  Last-Modified on asset-manifest.json, which is when the build was
//             written — the deploy time, straight from the server
//
// Release time is best effort. An unreadable header costs the "released" clause
// and nothing else, because the rest of the line does not depend on it.

const MANIFEST = '/asset-manifest.json'

/** The short content hash, e.g. "5fab0751", or null under the dev server. */
export const bundleHash = path => {
  const match = /main\.([a-f0-9]+)\.js$/.exec(path || '')
  return match ? match[1] : null
}

export const useBuildInfo = () => {
  const [releasedAt, setReleasedAt] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        // Same no-store + cache-buster as the update check: the manifest is
        // served with an etag but no cache-control, so a cached copy would
        // report the age of whatever the browser happened to keep.
        const res = await fetch(`${MANIFEST}?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return

        const header = res.headers.get('last-modified')
        if (!header) return

        const when = new Date(header)
        if (!cancelled && !isNaN(when.getTime())) setReleasedAt(when)
      } catch (e) {
        // Offline, or mid-deploy. The build line renders without an age.
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  return {
    version: process.env.REACT_APP_VERSION || null,
    env: process.env.REACT_APP_ENV || null,
    hash: bundleHash(runningBundle()),
    releasedAt
  }
}
